import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { updateOrganizationMCPServerPolicy } from '@kirin/api-client/api/mcp';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-errors';
import { trackEvent } from '@/lib/analytics';

export interface McpEnforcementActions {
  /** Store the decision org-wide; resolves, never rejects (failures toast). */
  setBlocked: (name: string, blocked: boolean) => Promise<void>;
  /**
   * True for this server from the click until the table has refetched, so a
   * second click cannot act on the stale row. Stable across renders: a click
   * re-renders the cells, not the column definitions.
   */
  isPending: (name: string) => boolean;
}

/**
 * Allow/Block one Inventory MCP server for the organization (KIR-4482) — the
 * endpoint-server counterpart of the gateway's `useGatewayControlToggle`:
 * blocking is confirmed by the caller first; this hook performs the write,
 * reports a failure with a toast, and refreshes the MCP table either way.
 */
export function useMcpEnforcementToggle(
  organizationId: string
): McpEnforcementActions {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const inFlight = useRef(new Set<string>());
  // Bumped on every change to `inFlight` so the table re-renders its cells.
  const [, setPendingVersion] = useState(0);

  const setBlocked = useCallback(
    async (name: string, blocked: boolean): Promise<void> => {
      if (inFlight.current.has(name)) return;
      inFlight.current.add(name);
      setPendingVersion(v => v + 1);
      try {
        await updateOrganizationMCPServerPolicy(
          apiClient,
          organizationId,
          name,
          {
            blocked,
          }
        );
        trackEvent('mcp_server_enforcement_change', {
          change: blocked ? 'block' : 'allow',
        });
      } catch (error) {
        toast({
          title: 'Failed to update MCP server policy',
          description: getApiErrorMessage(error, 'Please try again.'),
          variant: 'destructive',
        });
      } finally {
        // Refetch on failure too: another admin may have changed the row.
        await queryClient
          .invalidateQueries({ queryKey: queryKeys.mcp.all })
          .catch(() => undefined);
        inFlight.current.delete(name);
        setPendingVersion(v => v + 1);
      }
    },
    [organizationId, queryClient, toast]
  );

  const isPending = useCallback(
    (name: string) => inFlight.current.has(name),
    []
  );

  return { setBlocked, isPending };
}
